import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Car, Wrench, CalendarClock, MessageSquare, Bell, Menu, X, Upload,
  User, Settings, CreditCard, Building2, LogOut, ChevronDown, Heart,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrganization } from '../context/OrganizationContext';

/**
 * The signed-in chrome for the website.
 *
 * There was none. An earlier `components/Layout.tsx` defined an app shell but
 * was imported by nothing, so every page rendered bare: once someone signed in
 * there was no persistent way to reach Garage, Settings or Billing — only
 * whatever buttons the current page happened to draw. That is the gap between
 * "the pages exist" and "it is a website you can use". That dead file has since
 * been removed.
 *
 * Deliberately a top bar rather than the mobile app's bottom tabs. A bottom
 * bar on a 1440px screen is a phone layout stretched wide, which is exactly
 * what a website should not look like.
 */

const NAV = [
  { to: '/home', label: 'Home', icon: Home },
  { to: '/assessment', label: 'Judge a car', icon: Car },
  { to: '/garage', label: 'Garage', icon: Car },
  { to: '/receipts', label: 'Add records', icon: Upload },
  { to: '/maintenance', label: 'Costs', icon: Wrench },
  { to: '/upcoming-services', label: 'Services', icon: CalendarClock },
  { to: '/ask', label: 'Ask', icon: MessageSquare },
];

/**
 * Tab titles for the signed-in pages.
 *
 * Twenty of them carry no <SEO> block, and react-helmet only changes the title
 * when something sets it — so navigating Home -> Settings left the previous
 * page's title in the tab. Someone with several tabs open could not tell them
 * apart. Doing it here covers every shelled page at once; the marketing pages
 * keep their own <SEO> because they need real meta descriptions, not just a
 * title.
 */
const TITLES: Record<string, string> = {
  '/home': 'Home',
  '/garage': 'My garage',
  '/assessment': 'Judge a car',
  '/receipts': 'Add records',
  '/maintenance': 'Costs',
  '/upcoming-services': 'Upcoming services',
  '/ask': 'Ask CarsJudge',
  '/profile': 'Profile',
  '/settings': 'Settings',
  '/billing': 'Billing',
  '/notifications': 'Notifications',
  '/favorites': 'Saved cars',
  '/vehicle': 'Vehicle',
};

const ACCOUNT = [
  { to: '/profile', label: 'Profile', icon: User },
  { to: '/favorites', label: 'Saved cars', icon: Heart },
  { to: '/billing', label: 'Billing', icon: CreditCard },
  { to: '/settings', label: 'Settings', icon: Settings },
];

export const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { memberships } = useOrganization();
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);

  // Close the drawer on navigation, or it stays open over the new page.
  useEffect(() => { setMenuOpen(false); setAccountOpen(false); }, [location.pathname]);

  // A dropdown that only closes by clicking its own button is a trap.
  useEffect(() => {
    if (!accountOpen) return;
    const onDown = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) setAccountOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAccountOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [accountOpen]);

  const active = (to: string) =>
    location.pathname === to || location.pathname.startsWith(`${to}/`);

  useEffect(() => {
    const key = Object.keys(TITLES).find(
      (k) => location.pathname === k || location.pathname.startsWith(`${k}/`),
    );
    document.title = key ? `${TITLES[key]} | CarsJudge AI` : 'CarsJudge AI';
  }, [location.pathname]);

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-bg text-content">
      <header className="sticky top-0 z-40 border-b border-border bg-bg/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-shell items-center gap-3 px-4 sm:px-6">
          <Link to="/home" className="flex shrink-0 items-center gap-2">
            <img
              src="/01734CC0-A28E-4415-8CF9-08714C76263B.jpeg"
              alt=""
              className="h-8 w-8 rounded-lg"
            />
            <span className="hidden font-display text-lg font-bold tracking-tight sm:block">
              CarsJudge AI
            </span>
          </Link>

          <nav className="ml-4 hidden flex-1 items-center gap-1 lg:flex">
            {NAV.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                  active(to)
                    ? 'bg-brand text-white'
                    : 'text-content-secondary hover:bg-surface-2 hover:text-content'
                }`}
              >
                {label}
              </Link>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-1">
            <Link
              to="/notifications"
              aria-label="Notifications"
              className={`rounded-xl p-2.5 transition-colors hover:bg-surface-2 ${
                active('/notifications') ? 'text-brand' : 'text-content-secondary'
              }`}
            >
              <Bell size={19} />
            </Link>

            {/* Account menu */}
            <div className="relative hidden lg:block" ref={accountRef}>
              <button
                onClick={() => setAccountOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={accountOpen}
                className="flex items-center gap-1.5 rounded-xl px-2.5 py-2 text-sm font-medium text-content-secondary transition-colors hover:bg-surface-2 hover:text-content"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand/10 text-brand">
                  <User size={15} />
                </span>
                <ChevronDown size={15} />
              </button>

              {accountOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-56 overflow-hidden rounded-2xl border border-border bg-surface shadow-lg"
                >
                  {ACCOUNT.map(({ to, label, icon: Icon }) => (
                    <Link
                      key={to}
                      to={to}
                      role="menuitem"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-content hover:bg-surface-2"
                    >
                      <Icon size={16} className="text-content-tertiary" />
                      {label}
                    </Link>
                  ))}

                  <Link
                    to={memberships.length > 0 ? '/business' : '/business/create'}
                    role="menuitem"
                    className="flex items-center gap-3 border-t border-border px-4 py-2.5 text-sm text-content hover:bg-surface-2"
                  >
                    <Building2 size={16} className="text-content-tertiary" />
                    {memberships.length > 0 ? 'Fleet' : 'Set up a business'}
                  </Link>

                  <button
                    onClick={signOut}
                    role="menuitem"
                    className="flex w-full items-center gap-3 border-t border-border px-4 py-2.5 text-left text-sm text-content hover:bg-surface-2"
                  >
                    <LogOut size={16} className="text-content-tertiary" />
                    Sign out
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Menu"
              aria-expanded={menuOpen}
              className="rounded-xl p-2.5 text-content-secondary hover:bg-surface-2 lg:hidden"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {menuOpen && (
          <nav className="border-t border-border bg-bg px-4 py-3 lg:hidden">
            {NAV.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium ${
                  active(to) ? 'bg-brand text-white' : 'text-content-secondary hover:bg-surface-2'
                }`}
              >
                <Icon size={17} />
                {label}
              </Link>
            ))}

            <div className="my-2 border-t border-border" />

            {ACCOUNT.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-content-secondary hover:bg-surface-2"
              >
                <Icon size={17} />
                {label}
              </Link>
            ))}
            <Link
              to={memberships.length > 0 ? '/business' : '/business/create'}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-content-secondary hover:bg-surface-2"
            >
              <Building2 size={17} />
              {memberships.length > 0 ? 'Fleet' : 'Set up a business'}
            </Link>
            <button
              onClick={signOut}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-content-secondary hover:bg-surface-2"
            >
              <LogOut size={17} />
              Sign out
            </button>
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-shell px-0 sm:px-6">{children}</main>
    </div>
  );
};

export default AppShell;
